import _ from 'lodash';
import { Combobox } from '@headlessui/react';
import { ChangeEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { COMMON_TRANSLATE_KEYS } from '../../../constants/translate-keys';
import { SelectOption } from '../../../types';

type Props = {
  options: SelectOption[];
  query: string;
  selectedOption: SelectOption | null;
  onOptionSelect: (option: SelectOption) => void;
  onQueryChange: (query: string) => void;
  error?: string;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  skipInternalFiltering?: boolean;
};

const SelectWithSearch = ({
  options,
  query,
  onQueryChange,
  selectedOption,
  onOptionSelect,
  error,
  disabled,
  className,
  skipInternalFiltering,
  placeholder,
}: Props) => {
  const { t } = useTranslation();

  const filteredOptions = useMemo(() => {
    if (!skipInternalFiltering) {
      return options.filter((o) =>
        o.label.toLowerCase().includes(query.toLowerCase())
      );
    } else {
      return options;
    }
  }, [options, query, skipInternalFiltering]);

  const getInputClassName = useCallback(() => {
    let customClassName =
      'relative rounded-2xl text-sm py-4 pl-4 placeholder-mainBlue placeholder-opacity-50 w-full';
    if (error) {
      customClassName = customClassName + ' border-cancel border';
    }

    if (disabled) {
      customClassName = customClassName + ' bg-gray-800 text-gray-400';
    } else {
      customClassName = customClassName + ' bg-backGroundGray text-mainBlue';
    }

    return className + ' ' + customClassName;
  }, [error, disabled, className]);

  const getComboOptionClassName = useCallback(
    (active: boolean, selected: boolean) => {
      let optionClassName =
        'group flex flex-col items-start w-full p-2 text-sm text-start';

      if (active) {
        optionClassName += ' bg-secondaryBlack text-mainWhite';
      } else if (selected) {
        optionClassName += ' bg-dividerGray';
      }
      return optionClassName;
    },
    []
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (disabled) {
        return;
      }
      onQueryChange(e.target.value);
    },
    [disabled, onQueryChange]
  );

  const handleSelect = useCallback(
    (option: SelectOption) => {
      onOptionSelect(option);
      onQueryChange(option.label);
    },
    [onOptionSelect, onQueryChange]
  );

  return (
    <Combobox
      value={selectedOption}
      onChange={() => null}
      as="div"
      className="relative w-full"
      aria-disabled={disabled}
    >
      <Combobox.Input
        value={query}
        onChange={handleChange}
        className={getInputClassName()}
        placeholder={placeholder}
        type="text"
        autoComplete="one-time-code"
        autoCorrect='off'
        inputMode="text"
        name="select-with-search"
        aria-autocomplete="none"
      />
      <Combobox.Options
        className="absolute right-0 mt-2 origin-top-right bg-backGroundGray divide-y divide-dividerGray rounded-2xl text-mainWhite z-50 border border-additionalGray"
        style={{ width: `100%`, maxHeight: '10rem', overflowY: 'auto' }}
      >
        {filteredOptions.length === 0 && (
          <Combobox.Option value={''} onClick={() => null} disabled>
            {({ active, selected }) => (
              <button
                type="button"
                className={getComboOptionClassName(active, selected)}
              >
                {t(COMMON_TRANSLATE_KEYS.NO_FOUND_GROUPS)}
              </button>
            )}
          </Combobox.Option>
        )}
        {filteredOptions.map(({ label, value }) => (
          <Combobox.Option
            key={_.get(value, 'id') || value}
            onClick={() => handleSelect({ label, value })}
            value={value}
          >
            {({ active, selected }) => (
              <button
                type="button"
                className={getComboOptionClassName(active, selected)}
              >
                {label}
              </button>
            )}
          </Combobox.Option>
        ))}
      </Combobox.Options>
    </Combobox>
  );
};

export default SelectWithSearch;
